import { UserProfile } from '@/types/profile';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3013';

export interface CreateProfileRequest {
  profile_type: 'student' | 'teacher';
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  date_of_birth?: string;
  avatar_url?: string;
  bio?: string;
  location?: string;
  student_details?: {
    class_id?: string;
    class_grade?: string;
    school_name?: string;
    academic_interests?: string[];
    learning_goals?: string[];
    learning_style?: string;
    preferred_subjects?: string[];
    study_schedule?: string;
    achievements?: string[];
    extracurricular_activities?: string[];
  };
  teacher_details?: {
    qualifications?: string[];
    years_of_experience?: number;
    subjects_teaching?: string[];
    institution_name?: string;
    teaching_style?: string[];
    specializations?: string[];
    certifications?: string[];
    awards?: string[];
    publications?: string[];
    professional_development?: string[];
  };
}

export interface ProfileResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    user_id: string;
    profile_type: 'student' | 'teacher';
    first_name: string;
    last_name: string;
    phone?: string;
    date_of_birth?: string;
    avatar_url?: string;
    bio?: string;
    location?: string;
    student_details?: {
      class_id?: string;
      class_grade?: string;
      school_name?: string;
      academic_interests?: string[];
      learning_goals?: string[];
      learning_style?: string;
      preferred_subjects?: string[];
      study_schedule?: string;
      achievements?: string[];
      extracurricular_activities?: string[];
    };
    teacher_details?: {
      qualifications?: string[];
      years_of_experience?: number;
      subjects_teaching?: string[];
      institution_name?: string;
      teaching_style?: string[];
      specializations?: string[];
      certifications?: string[];
      awards?: string[];
      publications?: string[];
      professional_development?: string[];
    };
    profile_strength: number;
    last_calculated: string;
    created_at: string;
    updated_at: string;
  };
}

export interface CreateProfileResponse extends ProfileResponse {}
export interface GetProfileResponse extends ProfileResponse {}

export class ProfileService {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    token?: string
  ): Promise<T> {
    const url = `${this.baseURL}/api${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error(`Profile API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Get user profile - automatically fetches for the authenticated user
   */
  async getProfile(token: string): Promise<GetProfileResponse> {
    return this.makeRequest<GetProfileResponse>(
      '/profile',
      { method: 'GET' },
      token
    );
  }

  /**
   * Update user profile - automatically updates for the authenticated user
   */
  async updateProfile(profileData: Partial<CreateProfileRequest>, token: string): Promise<CreateProfileResponse> {
    return this.makeRequest<CreateProfileResponse>(
      '/profile',
      {
        method: 'PUT',
        body: JSON.stringify(profileData),
      },
      token
    );
  }
}

export const profileService = new ProfileService();
